import { zodResolver } from '@hookform/resolvers/zod';
import { type ForgotInput, forgotInput } from '@hudhuria/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { BackLink, FieldError, PrimaryButton, TextField } from '~/components/ui';
import { api, ok } from '~/lib/api';

export const Route = createFileRoute('/forgot-password')({ component: Forgot });

function Forgot() {
  const navigate = useNavigate();
  const form = useForm<ForgotInput>({ resolver: zodResolver(forgotInput), defaultValues: { email: '' } });
  const send = useMutation({
    mutationFn: (v: ForgotInput) => ok<{ ok: true }>(api.auth.forgot.$post({ json: v })),
    onError: (e) => form.setError('root', { message: e.message }),
  });

  return (
    <form
      onSubmit={form.handleSubmit((v) => send.mutate(v))}
      className="flex flex-1 animate-fade-in flex-col px-[26px] pt-safe-6 pb-safe"
      noValidate
    >
      <BackLink label="Sign in" onClick={() => void navigate({ to: '/sign-in' })} />
      <div className="mt-[26px] font-display text-[30px] font-bold tracking-[-0.02em]">Reset password</div>
      {send.isSuccess ? (
        <>
          <div className="mt-2 text-[15px] leading-normal text-slate-500">
            If <b className="text-ink">{form.getValues('email')}</b> has a Hudhuria account, a reset link is
            on its way. It's valid for one hour.
          </div>
          <div className="mt-auto">
            <PrimaryButton onClick={() => void navigate({ to: '/sign-in' })}>Back to sign in</PrimaryButton>
          </div>
        </>
      ) : (
        <>
          <div className="mt-2 text-[15px] text-slate-500">We'll email you a link to choose a new one.</div>
          <div className="mt-[30px]">
            <TextField
              label="Work email"
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              placeholder="you@institution.org"
              error={form.formState.errors.email?.message}
              {...form.register('email')}
            />
            <FieldError>{form.formState.errors.root?.message}</FieldError>
          </div>
          <div className="mt-auto pt-6">
            <PrimaryButton type="submit" loading={send.isPending}>
              Send reset link
            </PrimaryButton>
          </div>
        </>
      )}
    </form>
  );
}
